import React from 'react';
import PropTypes from 'prop-types';

import { Container } from 'components/Layout';

import { getInsightShopsLabel } from '../../utils';

import {
  getMediaStatus,
  RecapBillingCard,
  RecapBudgetTable,
  RecapMediaCard,
  RecapNameChange
} from './components';

const getTargetCriteriaLabels = target => {
  let parsed;
  try {
    parsed = JSON.parse(target?.frontData || '{}');
  } catch (error) {
    return [];
  }

  const labels = [];
  for (const subCategories of Object.values(parsed.operatorsGroups || {})) {
    for (const group of Object.values(subCategories || {})) {
      for (const criterion of group?.values || []) {
        if (criterion?.name) {
          labels.push(
            criterion.exclude ? `Hors ${criterion.name}` : criterion.name
          );
        }
      }
    }
  }
  return labels;
};

const ModalRecap = ({ view }) => {
  const {
    themeColors,
    campaign,
    budgetVersions,
    pendingNameChange,
    handleSaveMediaOrder,
    handleSaveLineItem,
    handleSaveExternalId,
    mediaOrders,
    concernedUpsertMedias,
    pendingChangesByMedia,
    upsertMediaBudgets,
    pendingUpsertPlan,
    lineItems,
    ads,
    availableCreatives,
    activableMedias,
    mediaLogosToDisplay,
    mediaOrderMargin,
    findInsightShop,
    insightShops,
    requestedGeoLabel,
    requestedInsightShops,
    insightUserTarget,
    matchedDepartments,
    activationCategoryKeys
  } = view;
  const { designSystem } = themeColors;

  if (!campaign.withDataPlan) {
    return null;
  }

  const getVolume = (activableMedia, withDataPlan) => {
    if (!activableMedia || !withDataPlan) {
      return 0;
    }
    const channels = (withDataPlan.channels || []).filter(channel =>
      activableMedia.channels.includes(channel.key)
    );
    let volume = 0;
    channels.forEach(channel => {
      if (channel?.mediaData) {
        const media = channel.mediaData.find(
          item => item.media === activableMedia.key && item.enabled
        );
        volume += media?.volume1?.total || 0;
      } else {
        volume += channel?.volume1?.total || 0;
      }
    });
    return volume;
  };

  const getAudienceCriteria = (lineItem, mediaKey) => {
    const criteria = [];
    const shopIds = findInsightShop(lineItem.filter);
    const appliedShops = insightShops.filter(shop =>
      shopIds.includes(shop._id)
    );
    const hasRequestedGeo = Boolean(requestedGeoLabel);
    const audienceShops = hasRequestedGeo
      ? requestedInsightShops
      : appliedShops;
    if (audienceShops.length > 0) {
      criteria.push(
        `${audienceShops.length} adresse${audienceShops.length > 1 ? 's' : ''}`
      );
    }
    const shopsLabel = hasRequestedGeo
      ? requestedGeoLabel
      : getInsightShopsLabel(appliedShops);
    if (shopsLabel) {
      criteria.push(shopsLabel);
    }
    insightUserTarget.forEach(target => {
      const targetCriteria = getTargetCriteriaLabels(target);
      if (targetCriteria.length > 0) {
        criteria.push(...targetCriteria);
      } else if (target?.name) {
        criteria.push(target.name);
      }
    });
    if (!hasRequestedGeo && matchedDepartments.length > 0) {
      const names = matchedDepartments
        .slice(0, 3)
        .map(department => department.name)
        .join(' / ');
      const extra =
        matchedDepartments.length > 3
          ? ` +${matchedDepartments.length - 3}`
          : '';
      criteria.push(`${names}${extra}`);
    }
    (activationCategoryKeys[mediaKey] || []).forEach(category =>
      criteria.push(category)
    );
    return criteria;
  };

  const mediaCards = mediaOrders.map((mediaOrder, mediaOrderIndex) => {
    const activableMedia = activableMedias.find(
      el => el.key === mediaOrder.activableMedia
    );
    const matchingLineItems = lineItems.filter(
      lineItem => lineItem.mediaOrderId === mediaOrder.id
    );
    const lineItemMargin = matchingLineItems.length
      ? mediaOrderMargin / matchingLineItems.length
      : 0;

    const audiences = matchingLineItems.map(lineItem => {
      const subtotal =
        lineItem.budgetMedia / matchingLineItems.length - lineItemMargin;
      const lineItemAds = ads.filter(
        ad => ad.mediaOrderId === mediaOrder.id && ad.lineItemId === lineItem.id
      );
      const creaAmount = lineItemAds.length ? subtotal / lineItemAds.length : 0;
      const creas = lineItemAds.map(ad => {
        const foundAsset = availableCreatives.find(
          el => el.id === ad.availableCreativeId
        );
        let asset = foundAsset;
        if (foundAsset) {
          const fileObject = foundAsset.mediaDatas?.[0]?.[
            mediaOrder.activableMedia
          ]?.find(el => el.key === 'file');
          if (fileObject) {
            asset = { ...foundAsset, uploadedAssetId: fileObject.value };
          }
        }
        return {
          id: ad.id,
          name: ad.availableCreative?.name || 'Asset introuvable',
          asset,
          mediaUrl: ad.postData?.mediaUrl || null,
          amount: creaAmount,
          externalId: ad.externalId,
          onSaveId: newExternalId =>
            handleSaveExternalId(ad.id, newExternalId, ad.leadGenFormId)
        };
      });

      return {
        id: lineItem.id,
        name: lineItem.name,
        subtotal,
        criteria: getAudienceCriteria(lineItem, mediaOrder.activableMedia),
        creas,
        externalId: lineItem.externalId,
        onSaveId: newExternalId =>
          handleSaveLineItem(lineItem.id, newExternalId)
      };
    });

    return {
      id: mediaOrder.id,
      logo: mediaLogosToDisplay[mediaOrderIndex],
      mediaKey: mediaOrder.activableMedia,
      mediaName: activableMedia?.name || mediaOrder.activableMedia,
      status: getMediaStatus(
        designSystem,
        activableMedia,
        mediaOrder.setupTask,
        mediaOrder.id,
        concernedUpsertMedias.includes(mediaOrder.activableMedia),
        campaign.lastUpsertTask
      ),
      volume:
        getVolume(activableMedia, campaign.withDataPlan) +
        getVolume(activableMedia, pendingUpsertPlan),
      total:
        mediaOrder.budgetMedia +
        (upsertMediaBudgets[mediaOrder.activableMedia] || 0) -
        mediaOrderMargin,
      audiences,
      pendingChange:
        activableMedia?.canSetUp === true
          ? pendingChangesByMedia[mediaOrder.activableMedia] || null
          : null,
      externalId: mediaOrder.externalId,
      onSaveId: newExternalId =>
        handleSaveMediaOrder({ ...mediaOrder, externalId: newExternalId })
    };
  });

  return (
    <Container
      flex
      style={{ width: '100%', flexDirection: 'column', alignItems: 'stretch' }}
    >
      {budgetVersions.length > 1 ? (
        <RecapBudgetTable versions={budgetVersions} />
      ) : (
        <RecapBillingCard version={budgetVersions[0] || null} />
      )}
      {pendingNameChange && <RecapNameChange nameChange={pendingNameChange} />}
      {mediaCards.map(card => (
        <RecapMediaCard card={card} key={card.id} />
      ))}
    </Container>
  );
};

ModalRecap.propTypes = {
  view: PropTypes.shape().isRequired
};

export default ModalRecap;
